/**
 * SelectStationUseCase Implementation
 *
 * Orchestrates station selection and scenario loading through the StationGateway.
 * Use-cases coordinate entities and gateways but don't implement business rules.
 *
 * Clean Architecture:
 * - usecase-orchestrates-not-implements: Delegates to gateway
 * - usecase-single-responsibility: Only handles selection orchestration
 * - usecase-explicit-dependencies: Gateway injected via constructor
 */

import type {
  SelectStationUseCase,
  SelectStationInput,
  SelectStationOutput,
} from '@/domain/use-cases';
import type { StationGateway } from '@/domain/gateways';
import { getScenarioCount } from '@/domain/entities';
import { Logger } from '@/app/utils/logger';

const LOG_TAG = 'select-station-usecase';

/**
 * Implementation of SelectStationUseCase.
 *
 * Selects a station and ensures its scenarios are loaded
 * through the injected gateway.
 */
export class SelectStationUseCaseImpl implements SelectStationUseCase {
  constructor(private readonly stationGateway: StationGateway) {}

  /**
   * Execute the use-case to select a station and load its scenarios.
   *
   * @param input - Station to select and optional forceRefresh flag
   * @returns Promise with station including loaded scenarios
   */
  async execute(input: SelectStationInput): Promise<SelectStationOutput> {
    const { station, forceRefresh = false } = input;

    try {
      Logger.info(
        `Executing SelectStationUseCase: ${station.displayName} (forceRefresh=${forceRefresh})`,
        LOG_TAG
      );

      const stationWithScenarios = await this.stationGateway.fetchStationScenarios(
        station,
        forceRefresh
      );

      const scenarioCount = getScenarioCount(stationWithScenarios);
      Logger.info(
        `Station selected: ${stationWithScenarios.displayName}, ${scenarioCount} scenarios`,
        LOG_TAG
      );

      return {
        station: stationWithScenarios,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(
        `SelectStationUseCase failed for ${station.displayName}: ${message}`,
        LOG_TAG
      );
      throw error;
    }
  }
}

/**
 * Factory function to create SelectStationUseCaseImpl with gateway.
 *
 * @param stationGateway - Gateway for station operations
 * @returns Configured SelectStationUseCase instance
 */
export function createSelectStationUseCase(
  stationGateway: StationGateway
): SelectStationUseCase {
  return new SelectStationUseCaseImpl(stationGateway);
}
